import { DISPLAY_CURRENCIES } from "@/config/pricing/exchange-rates";
import { useCallerCountry } from "@/hooks/useCallerCountry";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, Check } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
function FlagImg({ code, className }: { code: string; className?: string }) {
  // Use ISO2 directly - flagcdn is ISO2-keyed. Dial-code lookup is ambiguous
  // (e.g. +1 matches US, CA, AG, ...), so go straight from country code.
  const url = `https://flagcdn.com/${code.toLowerCase()}.svg`;

  return (
    <img
      src={url}
      alt=""
      aria-hidden
      loading="lazy"
      className={cn(
        "inline-block w-[1.1em] h-[0.8em] object-cover rounded-[2px] ring-1 ring-black/5",
        className
      )}
    />
  );
}

// Deduplicate currencies — many countries share EUR (DE, FR, NL, BE, IT, IE).
// Show one row per distinct currency code so the picker doesn't repeat "EUR".
const CURRENCY_OPTIONS = (() => {
  const seen = new Set<string>();
  return Object.entries(DISPLAY_CURRENCIES).filter(([, cfg]) => {
    if (seen.has(cfg.code)) return false;
    seen.add(cfg.code);
    return true;
  });
})();


export function CurrencySwitcher() {
  const { callerCountry, displayCurrency, setCountryOverride } = useCallerCountry();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium bg-muted/50 hover:bg-muted transition-colors border border-border/50"
          aria-label="Change currency"
        >
          <FlagImg code={callerCountry} />
          <span className="text-muted-foreground">{displayCurrency}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-52 p-1.5" sideOffset={8}>
        <div className="space-y-0.5">
          {CURRENCY_OPTIONS.map(([code, config]) => {
            const isActive = callerCountry === code;
            return (
              <button
                key={code}
                onClick={() => { setCountryOverride(code); setOpen(false); }}
                className={cn(
                  "flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "hover:bg-muted text-foreground"
                )}
              >
                <FlagImg code={code} />
                <span className="flex-1 text-left">{config.code}</span>
                <span className="text-xs text-muted-foreground">{config.name}</span>
                {isActive && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
